import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { Layout, ErrorBoundary } from './components';
import { RateLimitSnackbar, PwaInstallBanner, PwaUpdateToast } from './components/shared';
import { DevBanner } from '@katapult/ui';
import {
  HomePage,
  LoginPage,
  RegisterPage,
  SSOCallbackPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  ReferralRedirectPage,
  BoardsListPage,
  BoardDetailPage,
  InviteJoinPage,
  CreateBoardPage,
  ProfileSettingsPage,
  MyReferralsPage,
  CreditsPage,
  SubscriptionsPage,
  OfflinePlayersPage,
  ConnectionsPage,
  TemplatesPage,
  TemplateEditorPage,
  AccountingPage,
  AccountingDetailPage,
  MyBalancesPage,
  MyManagerDetailPage,
  ThemeStorePage,
  NotFoundPage,
  PublicBoardViewPage,
  WinnersPage,
  TermsOfServicePage,
  PrivacyPolicyPage,
  SupportHomePage,
  GettingStartedPage,
  BoardManagementPage,
  GameMechanicsPage,
  PayoutsPage,
  AccountingSupportPage,
  CreditsSupportPage,
  ThemesSupportPage,
  NotificationsSupportPage,
  ParticipationModesPage,
  FAQPage,
  GlossaryPage,
  DataDeletionPage,
  LiveGamesSupportPage,
  BlogHomePage,
  BlogCategoryPage,
  BlogTagPage,
  BlogPostPage,
  BlogAdminPage,
  NotificationsPage,
} from './pages';
import { MyThemesPage, TransactionHistoryPage, NotificationPreferencesPage } from './pages/profile';
import { ProfileLayout } from './components/profile';
import {
  AdminDashboardPage,
  UsersManagementPage,
  BoardsManagementPage,
  PaymentsPage,
  TransactionsPage,
  PaymentSettingsPage,
  ProductsPage,
  CouponsPage,
  AdminSubscriptionsPage,
  AuditLogPage,
  ReferralTiersPage,
  ReferralsPage,
  EmailTemplatesPage,
  EmailLogsPage,
  WebhookEventsPage,
  ThemesPage,
  TierConfigPage,
  AdminNotificationsPage,
  AdminTasksPage,
  SportsConfigPage,
} from './pages/admin';
import { AdminLayout } from './components/admin';
import { isAdmin } from './types';
import { useAuth } from './hooks';
import { AuthProvider, CreditsProvider, NotificationProvider } from './contexts';

// Create MUI theme - matching SquareUp logo style
const theme = createTheme({
  cssVariables: false,
  palette: {
    primary: {
      main: '#509ed3', // SquareUp logo blue (from SVG)
      light: '#7BB8E0',
      dark: '#3A7FB0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2D3748', // Dark charcoal for contrast
      light: '#4A5568',
      dark: '#1A202C',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F7FAFC',
      paper: '#ffffff',
    },
    text: {
      primary: '#2D3748',
      secondary: '#718096',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightMedium: 600,
    fontWeightBold: 700,
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#2D3748', // Dark charcoal header
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 6,
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#3A8FC4',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
        },
      },
    },
  },
});

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  if (!isAuthenticated) {
    // Preserve the intended destination for redirect after login
    const redirectTo = location.pathname + location.search;
    return <Navigate to={`/login?redirectTo=${encodeURIComponent(redirectTo)}`} replace />;
  }

  return <>{children}</>;
};

// Profile route wrapper - uses ProfileLayout
const ProfileRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    const redirectTo = location.pathname + location.search;
    return <Navigate to={`/login?redirectTo=${encodeURIComponent(redirectTo)}`} replace />;
  }

  return <ProfileLayout>{children}</ProfileLayout>;
};

// Admin route wrapper - requires admin role and uses AdminLayout
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    const redirectTo = location.pathname + location.search;
    return <Navigate to={`/login?redirectTo=${encodeURIComponent(redirectTo)}`} replace />;
  }

  if (!isAdmin(user)) {
    return <Navigate to="/" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

// Wrapper for routes that need the main Layout
const LayoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  // Routes that have their own layout (no main Layout wrapper)
  const standaloneRoutes = ['/b/'];
  const isStandaloneRoute = standaloneRoutes.some(route => location.pathname.startsWith(route));

  if (isStandaloneRoute) {
    return null;
  }

  return <Layout>{children}</Layout>;
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <BrowserRouter>
            <AuthProvider>
              <CreditsProvider>
                <NotificationProvider>
            {/* Standalone routes - have their own layout */}
            <Routes>
              <Route path="/b/:boardSlug/winners" element={<WinnersPage />} />
              <Route path="/b/:boardSlug" element={<PublicBoardViewPage />} />
            </Routes>
            {/* Routes wrapped in main Layout */}
            <LayoutWrapper>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/auth/callback" element={<SSOCallbackPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
              <Route path="/r/:code" element={<ReferralRedirectPage />} />

              {/* Legal pages - public */}
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />

              {/* Protected routes */}
              <Route
                path="/boards"
                element={
                  <ProtectedRoute>
                    <BoardsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/boards/:boardIdOrSlug"
                element={
                  <ProtectedRoute>
                    <BoardDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/boards/:boardIdOrSlug/invite/:inviteCode"
                element={<InviteJoinPage />}
              />
              <Route
                path="/boards/:boardIdOrSlug/:tab"
                element={
                  <ProtectedRoute>
                    <BoardDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-board"
                element={
                  <ProtectedRoute>
                    <CreateBoardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProfileRoute>
                    <ProfileSettingsPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/profile/referrals"
                element={
                  <ProfileRoute>
                    <MyReferralsPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/profile/themes"
                element={
                  <ProfileRoute>
                    <MyThemesPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/profile/transactions"
                element={
                  <ProfileRoute>
                    <TransactionHistoryPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/profile/transactions/:tab"
                element={
                  <ProfileRoute>
                    <TransactionHistoryPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/profile/offline-players"
                element={
                  <ProfileRoute>
                    <OfflinePlayersPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/profile/connections"
                element={
                  <ProfileRoute>
                    <ConnectionsPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/profile/templates"
                element={
                  <ProfileRoute>
                    <TemplatesPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/profile/templates/:templateId"
                element={
                  <ProfileRoute>
                    <TemplateEditorPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/profile/notifications"
                element={
                  <ProfileRoute>
                    <NotificationPreferencesPage />
                  </ProfileRoute>
                }
              />
              <Route
                path="/credits"
                element={
                  <ProtectedRoute>
                    <CreditsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute>
                    <SubscriptionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/offline-players"
                element={
                  <ProtectedRoute>
                    <OfflinePlayersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/connections"
                element={
                  <ProtectedRoute>
                    <ConnectionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounting"
                element={
                  <ProtectedRoute>
                    <AccountingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounting/user/:userId"
                element={
                  <ProtectedRoute>
                    <AccountingDetailPage type="user" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounting/offline/:offlinePlayerId"
                element={
                  <ProtectedRoute>
                    <AccountingDetailPage type="offline" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/balances"
                element={
                  <ProtectedRoute>
                    <MyBalancesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/balances/manager/:managerId"
                element={
                  <ProtectedRoute>
                    <MyManagerDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/themes"
                element={
                  <ProtectedRoute>
                    <ThemeStorePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/themes/:category"
                element={
                  <ProtectedRoute>
                    <ThemeStorePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />

              {/* Support routes - public */}
              <Route path="/support" element={<SupportHomePage />} />
              <Route path="/support/getting-started" element={<GettingStartedPage />} />
              <Route path="/support/boards" element={<BoardManagementPage />} />
              <Route path="/support/game-mechanics" element={<GameMechanicsPage />} />
              <Route path="/support/payouts" element={<PayoutsPage />} />
              <Route path="/support/accounting" element={<AccountingSupportPage />} />
              <Route path="/support/credits" element={<CreditsSupportPage />} />
              <Route path="/support/themes" element={<ThemesSupportPage />} />
              <Route path="/support/notifications" element={<NotificationsSupportPage />} />
              <Route path="/support/participation-modes" element={<ParticipationModesPage />} />
              <Route path="/support/faq" element={<FAQPage />} />
              <Route path="/support/glossary" element={<GlossaryPage />} />
              <Route path="/support/data-deletion" element={<DataDeletionPage />} />
              <Route path="/support/live-games" element={<LiveGamesSupportPage />} />

              {/* Blog routes - public */}
              <Route path="/blog" element={<BlogHomePage />} />
              <Route path="/blog/category/:category" element={<BlogCategoryPage />} />
              <Route path="/blog/tag/:tag" element={<BlogTagPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              {/* Blog admin - protected by AdminRoute */}
              <Route
                path="/blog/admin"
                element={
                  <AdminRoute>
                    <BlogAdminPage />
                  </AdminRoute>
                }
              />

              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboardPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <UsersManagementPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/boards"
                element={
                  <AdminRoute>
                    <BoardsManagementPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/payments"
                element={
                  <AdminRoute>
                    <PaymentsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/transactions"
                element={
                  <AdminRoute>
                    <TransactionsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <AdminRoute>
                    <PaymentSettingsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/products"
                element={
                  <AdminRoute>
                    <ProductsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/themes"
                element={
                  <AdminRoute>
                    <ThemesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/coupons"
                element={
                  <AdminRoute>
                    <CouponsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <AdminRoute>
                    <AdminSubscriptionsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/audit"
                element={
                  <AdminRoute>
                    <AuditLogPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/referral-tiers"
                element={
                  <AdminRoute>
                    <ReferralTiersPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/tier-config"
                element={
                  <AdminRoute>
                    <TierConfigPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/referrals"
                element={
                  <AdminRoute>
                    <ReferralsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/email/templates"
                element={
                  <AdminRoute>
                    <EmailTemplatesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/email/logs"
                element={
                  <AdminRoute>
                    <EmailLogsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/email/webhooks"
                element={
                  <AdminRoute>
                    <WebhookEventsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <AdminRoute>
                    <AdminNotificationsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/tasks"
                element={
                  <AdminRoute>
                    <AdminTasksPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/sports"
                element={
                  <AdminRoute>
                    <SportsConfigPage />
                  </AdminRoute>
                }
              />

              {/* Catch-all 404 */}
              <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </LayoutWrapper>
                </NotificationProvider>
              </CreditsProvider>
            </AuthProvider>
          </BrowserRouter>
        </ErrorBoundary>
        <RateLimitSnackbar />
        <PwaInstallBanner />
        <PwaUpdateToast />
        <DevBanner />
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
